<template>
  <div
    class="boost-opportunity-chart-tooltip tw:absolute tw:z-10 tw:w-[240px] tw:-translate-x-1/2 tw:-translate-y-[calc(100%+8px)] tw:bg-white tw:rounded-lg tw:shadow-[0_1px_4px_0_rgba(41,51,57,0.5)] tw:overflow-hidden tw:pointer-events-auto"
    :style="{ left: `${position.left}px`, top: `${position.top}px` }"
    @mouseenter="$emit('mouseenter')"
    @mouseleave="$emit('mouseleave')"
  >
    <div class="tw:p-4">
      <div v-if="isOpportunity" class="tw:mb-4">
        <span
          class="tw:inline-flex tw:items-center tw:gap-1 tw:text-xs tw:font-bold tw:px-2 tw:py-1 tw:rounded-md tw:bg-orange-200"
        >
          <img :src="opportunityIcon" alt="" aria-hidden="true" class="tw:w-3 tw:h-3" />
          {{ opportunityBadgeLabel }}
        </span>
      </div>

      <p class="tw:text-xl tw:font-bold tw:mb-3">{{ formattedDate }}</p>

      <div v-if="item.demandType" class="tw:mb-2">
        <p class="tw:text-xs tw:font-bold tw:text-gray-700 tw:mb-1">{{ $t('rc_search_demand') }}</p>
        <div class="tw:h-2 tw:bg-gray-200 tw:rounded-full tw:overflow-hidden">
          <div
            class="tw:h-full tw:rounded-full"
            :style="{ width: `${demandBarWidth}%`, backgroundColor: demandBarColor }"
          />
        </div>
        <p class="tw:text-xs tw:text-gray-700 tw:text-right tw:mt-1">{{ demandLabel }}</p>
      </div>

      <div v-if="item.topPositionShareType">
        <p class="tw:text-xs tw:font-bold tw:text-gray-700 tw:mb-1">{{ $t('rc_hotel_position_share') }}</p>
        <div class="tw:h-2 tw:bg-gray-200 tw:rounded-full tw:overflow-hidden">
          <div
            class="tw:h-full tw:rounded-full"
            :style="{ width: `${topPositionShareBarWidth}%`, backgroundColor: '#D87125' }"
          />
        </div>
        <p class="tw:text-xs tw:text-gray-700 tw:text-right tw:mt-1">{{ topPositionShareLabel }}</p>
      </div>
    </div>

    <div v-if="isOpportunity && card" class="tw:border-t tw:border-gray-300">
      <button
        type="button"
        class="tw:flex tw:w-full tw:justify-between tw:items-center tw:px-3 tw:py-3 tw:border-b tw:border-gray-300 tw:cursor-pointer tw:hover:bg-gray-50 tw:text-left"
        @click="goToSmartBoost(card, card.boostCpaLevelA)"
      >
        <div>
          <p class="tw:text-base tw:font-bold">
            {{ formatPercentage(card.boostCpaLevelA, 0, 0) }} {{ $t('rc_smartboost_history_commission') }}
          </p>
          <p class="tw:text-xs" :class="getShareIncreaseLabel(card, card.boostATpShareIncrease).colorClass">
            {{ getShareIncreaseLabel(card, card.boostATpShareIncrease).text }}
          </p>
        </div>
        <ChevronRight class="tw:fill-gray-800 tw:shrink-0 tw:size-6" />
      </button>

      <button
        type="button"
        class="tw:flex tw:w-full tw:justify-between tw:items-center tw:px-3 tw:py-3 tw:cursor-pointer tw:hover:bg-gray-50 tw:text-left"
        @click="goToSmartBoost(card, card.boostCpaLevelB)"
      >
        <div>
          <p class="tw:text-base tw:font-bold">
            {{ formatPercentage(card.boostCpaLevelB, 0, 0) }} {{ $t('rc_smartboost_history_commission') }}
          </p>
          <p class="tw:text-xs" :class="getShareIncreaseLabel(card, card.boostBTpShareIncrease).colorClass">
            {{ getShareIncreaseLabel(card, card.boostBTpShareIncrease).text }}
          </p>
        </div>
        <ChevronRight class="tw:fill-gray-800 tw:shrink-0 tw:size-6" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ChevronRight from '@/components/icons/ChevronRight.svg?component'

import { useFormatting } from '@/composables/useFormatting'
import {
  DEMAND_LABEL_BY_TYPE,
  getDemandBarWidth,
  getSearchDemandBarColor,
  hasSearchDemandOpportunity,
  useBoostOpportunityNavigation,
} from '@/composables/useBoostOpportunityNavigation'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  card: {
    type: Object,
    default: null,
  },
  position: {
    type: Object,
    required: true,
  },
})

defineEmits(['mouseenter', 'mouseleave'])

const { t } = useI18n()
const { formatDate, formatPercentage } = useFormatting()
const { getShareIncreaseLabel, goToSmartBoost } = useBoostOpportunityNavigation()

const starIcon = new URL('/src/assets/img/rate-connect/star-icon.svg', import.meta.url).href
const circleIcon = new URL('/src/assets/img/rate-connect/circle-icon.svg', import.meta.url).href

const isOpportunity = computed(() => hasSearchDemandOpportunity(props.item.opportunityType))

const opportunityIcon = computed(() => (props.item.opportunityType === 'HOT_OPPORTUNITY' ? starIcon : circleIcon))

const opportunityBadgeLabel = computed(() => {
  if (props.item.opportunityType === 'HOT_OPPORTUNITY') return t('rc_top_opportunity')
  if (props.item.opportunityType === 'OPPORTUNITY') return t('rc_good_opportunity')
  return ''
})

const formattedDate = computed(() => {
  if (!props.item.searchedCheckinDate) return '-'

  return formatDate(new Date(props.item.searchedCheckinDate), {
    month: 'long',
    day: 'numeric',
  })
})

const getDemandLabel = type => {
  return type && DEMAND_LABEL_BY_TYPE[type] ? t(DEMAND_LABEL_BY_TYPE[type]) : '-'
}

const demandLabel = computed(() => getDemandLabel(props.item.demandType))
const topPositionShareLabel = computed(() => getDemandLabel(props.item.topPositionShareType))
const demandBarWidth = computed(() => getDemandBarWidth(props.item.demandType))
const topPositionShareBarWidth = computed(() => getDemandBarWidth(props.item.topPositionShareType))
const demandBarColor = computed(() => getSearchDemandBarColor(props.item.opportunityType))
</script>
